import { Dayjs } from 'dayjs';
import { ShiftList, StaffingType } from '@libs/models/shifts/shift';
import { TIMEZONE_FALLBACK } from '@libs/config';
import { filterToQueryParams } from '../../utils';
import { request } from '../../request';
import { FiltersCollection } from '@libs/store/filters';

export async function getShiftToShare(params: {
  startDate?: Dayjs;
  endDate?: Dayjs;
  timezone: string;
  filters?: FiltersCollection;
}): Promise<ShiftList[]> {
  const { timezone, startDate, endDate, filters = {} } = params;
  const applyFilters = filterToQueryParams(filters, {
    removeEmpty: true,
    renameMap: { statuses: 'status' },
  });
  const response = await request.get<ShiftToShare[]>('/v2/shifts/to-share', {
    params: {
      search: '',
      ...applyFilters,
      startDate: startDate?.toServerFilterDate(),
      endDate: endDate?.toServerFilterDate(),
      timeZone: timezone || TIMEZONE_FALLBACK,
    },
  });

  return response.data.map((shift) => ({
    ...shift,
    externalId: shift.externalIdAsString || '',
    status: shift.status as ShiftList['status'],
    staff: {
      id: shift.staff?.id || '',
      firstName: shift.staff?.firstName || '',
      lastName: shift.staff?.lastName || '',
      fullName: shift.staff ? [shift.staff.firstName, shift.staff.lastName].filter(Boolean).join(' ') : '',
      imageURL: shift.staff?.imageURL || '',
      nurseQualifications: shift.staff?.nurseQualifications || [],
    },
    specialty: {
      id: shift.speciality?.id,
      name: shift.speciality?.title,
      category: shift.speciality?.category,
    },
    timeZone: shift.startDate.timeZone,
    startDate: shift.startDate.dateTime,
    endDate: shift.endDate.dateTime,
    nurseQualifications: shift.staffNurseQualificationFilters || [],
    otherQualifications: shift.staffOtherQualificationFilters || [],
    // recurrence: shift.shiftSeriesId ? 'series' : shift.shiftRotationId ? 'rotation' : 'single',
    recurrence: shift.recurrenceType
      ? (shift.recurrenceType.toLowerCase() as 'single' | 'series' | 'rotation')
      : shift.shiftRotationId
        ? 'rotation'
        : shift.shiftSeriesId
          ? 'series'
          : 'single',
    recurrenceId: shift.shiftRecurrenceId,
    notes: shift.note ? [shift.note] : [],
  }));
}

type ShiftToShare = {
  id: string;
  externalIdAsString: string | null;
  canHasMultipleAssignee: boolean;
  staffNurseQualificationFilters: {
    id: string;
    name: string;
    abbreviation: string;
    category: string;
  }[];
  agenciesForShare: {
    id: string; //'staffingpartner-00000000000000000000000000';
    logoURL: string;
    name: string;
    tenantId: string;
  }[];
  staffOtherQualificationFilters: {
    id: string;
    name: string;
    abbreviation: string;
    category: string;
  }[];
  staff: {
    id: string;
    firstName: string;
    lastName: string;
    imageURL: string;
    nurseQualifications: {
      id: string;
      name: string;
      abbreviation: string;
      category: string;
    }[];
  } | null;
  startDate: {
    dateTime: string;
    timeZone: string;
  };
  endDate: {
    dateTime: string;
    timeZone: string;
  };
  department: {
    id: string;
    label: string;
  };
  subDepartment: {
    id: string;
    label: string;
  } | null;
  speciality: {
    id: string;
    title: string;
    category: string;
  };
  tags: string[];
  note: string;
  isUrgent: boolean;
  cancellingReason: string | null;
  unassignReason: string | null;
  chosenQualifications: {
    id: string;
    name: string;
    abbreviation: string;
    category: string;
  }[];
  status: ShiftList['status'];
  location: {
    id: string;
    name: string;
  };
  staffingType: StaffingType;
  shiftSeriesId?: string | null;
  shiftRotationId?: string | null;
  recurrenceType: 'Series' | 'Rotation';
  shiftRecurrenceId: string;
  isShared: boolean;
  isArchive: boolean;
};
